import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Box, Chip, Popover, Tooltip } from '@mui/material';
import AddReactionOutlinedIcon from '@mui/icons-material/AddReactionOutlined';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { supabase } from '../supabaseClient';
import { AuthContext, MyContext } from '../context/AuthContext';

interface Reaction {
  id: string;
  user_record_id: string;
  emoji: string;
}

function EmojiReactions({
  objectId,
  objectType = 'posts',
}: {
  objectId: string;
  objectType?: string;
}) {
  const userRecord = useContext<MyContext>(AuthContext).userRecord;
  const [reactions, setReactions] = useState<Reaction[]>([]);
  const [pickerAnchor, setPickerAnchor] = useState<null | HTMLElement>(null);
  // Unique per-mount suffix so two EmojiReactions for the same objectId
  // on the same page don't collide on a shared Supabase channel name
  // (supabase.channel(name) is idempotent per name — second .on() after
  // subscribe throws).
  const channelIdRef = useRef<string>(Math.random().toString(36).slice(2, 10));

  const fetchReactions = useCallback(async () => {
    const { data: rows } = await supabase
      .from('reactions')
      .select('id, user_record_id, emoji')
      .eq('object_type', objectType)
      .eq('object_id', objectId);
    if (rows) setReactions(rows as Reaction[]);
  }, [objectId, objectType]);

  useEffect(() => {
    fetchReactions();
    const channel = supabase
      .channel(`reactions-${objectId}-${channelIdRef.current}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'reactions',
        filter: `object_id=eq.${objectId}`,
      }, fetchReactions)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchReactions, objectId]);

  const myReaction = userRecord
    ? reactions.find((r) => r.user_record_id === userRecord.id)?.emoji ?? null
    : null;

  // Group by emoji → count
  const groups = reactions.reduce<Record<string, number>>((acc, r) => {
    acc[r.emoji] = (acc[r.emoji] ?? 0) + 1;
    return acc;
  }, {});
  const sorted = Object.entries(groups).sort((a, b) => b[1] - a[1]);

  const handleToggle = async (emoji: string) => {
    if (!userRecord) return;
    const existing = reactions.find((r) => r.user_record_id === userRecord.id);
    if (existing) {
      // Remove or switch: delete first
      await supabase.from('reactions').delete().eq('id', existing.id);
      if (existing.emoji !== emoji) {
        // Switch to a different emoji
        await supabase.from('reactions').insert({
          user_record_id: userRecord.id,
          object_type: objectType,
          object_id: objectId,
          emoji,
        });
      }
    } else {
      await supabase.from('reactions').insert({
        user_record_id: userRecord.id,
        object_type: objectType,
        object_id: objectId,
        emoji,
      });
    }
    setPickerAnchor(null);
    await fetchReactions();
  };

  const handlePickerSelect = (emojiObj: any) => {
    handleToggle(emojiObj.native ?? emojiObj.id);
  };

  if (sorted.length === 0 && !userRecord) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.75, mb: 0.25 }}>
      {sorted.map(([emoji, count]) => (
        <Tooltip key={emoji} title={myReaction === emoji ? 'Remove reaction' : 'React'}>
          <Chip
            label={`${emoji} ${count}`}
            size="small"
            onClick={() => handleToggle(emoji)}
            variant={myReaction === emoji ? 'filled' : 'outlined'}
            sx={{
              height: 22,
              fontSize: '0.8rem',
              cursor: 'pointer',
              fontFamily: 'inherit',
              borderColor: myReaction === emoji ? 'primary.main' : 'divider',
              bgcolor: myReaction === emoji ? 'primary.main' : 'transparent',
              color: myReaction === emoji ? 'primary.contrastText' : 'text.secondary',
              '& .MuiChip-label': { px: 0.75 },
              '&:hover': { borderColor: 'primary.light', bgcolor: myReaction === emoji ? 'primary.dark' : 'action.hover' },
            }}
          />
        </Tooltip>
      ))}

      {userRecord && (
        <>
          <Tooltip title="Add reaction">
            <Box
              component="span"
              onClick={(e) => setPickerAnchor(e.currentTarget as HTMLElement)}
              sx={{
                display: 'inline-flex', alignItems: 'center', cursor: 'pointer',
                color: 'text.disabled', '&:hover': { color: 'text.secondary' },
                transition: 'color 0.15s',
              }}
            >
              <AddReactionOutlinedIcon sx={{ fontSize: 16 }} />
            </Box>
          </Tooltip>
          <Popover
            open={Boolean(pickerAnchor)}
            anchorEl={pickerAnchor}
            onClose={() => setPickerAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{ sx: { boxShadow: 6, borderRadius: 2 } }}
          >
            <Picker
              data={data}
              onEmojiSelect={handlePickerSelect}
              theme="auto"
              previewPosition="none"
              skinTonePosition="none"
            />
          </Popover>
        </>
      )}
    </Box>
  );
}

export default EmojiReactions;
